"use client";

import Image from "next/image";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { Offcanvas } from "react-bootstrap";
import { X, ChevronRight, XCircle } from "react-feather";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { colorArray } from "@/utils/Colors";
import locations from "@/utils/locations";
import RecentlyViewed from "@/components/UI/Carousels/RecentlyViewed";
import Listing from "@/components/UI/Carousels/Listing";
import PlaceHolderListingNew from "@/components/UI/Carousels/PlaceHolderListingNew";
import MessageCard from "./MessageCard";
import classes from "./ShowListings.module.css";
import BrandFilter from "./BrandFilter";
import BuyPaginationBar from "./BuyPaginationBar";
import ColorFilter from "./ColorFilter";
import DimensionsFilter from "./DimensionsFilter";
import CategoriesDiscover from "./CategoriesDiscover";
import LocationFilter from "./LocationFilter";
import MaterialFilter from "./MaterialFilter";
import NewCategoriesFilter from "./NewCategoriesFilter";
import PriceFilter from "./PriceFilter";
import SavedSearches from "./SavedSearches";
import Sorting from "./Sorting";
import StyleFilter from "./StyleFilter";
import { useAuthContext } from "@/context";
import { useListingsContext } from "@/context/listings";
import getLocation from "@/utils/getLocation";

const ShowListings = ({ params }) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [isClient, setIsClient] = useState();

  useEffect(() => {
    setIsClient(true);
  }, []);

  const segments = pathname?.split("/").filter(Boolean); // Split and remove any empty strings

  let categoryParam = params.category ? params.category : "";
  let optionParam = params.subCategory ? params.subCategory : "";
  let typeParam = params.type ? params.type : "";
  let brandParam = params.brand ? params.brand : "";
  let locationParam = params.location ? params.location : "";
  let styleParam = params.style ? params.style : "";
  let roomParam = params.room ? params.room : "";

  const { auth } = useAuthContext();
  const { listings, LOAD_LISTINGS } = useListingsContext();

  const {
    uniqueBrands,
    uniqueStyles,
    uniqueMaterials,
    uniqueCategories,
    uniqueSubCategories,
    uniqueTypes,
  } = listings;

  const [show, setShow] = useState(false);
  const [active, setActive] = useState(false);
  const [sold, setSold] = useState(false);

  const [option, setOption] = useState();
  const [subType, setSubType] = useState();
  const [brand, setBrand] = useState();
  const [style, setStyle] = useState();
  const [room, setRoom] = useState();
  const [material, setMaterial] = useState();
  const [category, setCategory] = useState();
  const [condition, setCondition] = useState();
  const [locationCity, setLocationCity] = useState();
  const [color, setColor] = useState();

  const [op, setOp] = useState();
  const [tag, setTag] = useState();
  const [discount, setDiscount] = useState();
  const [minPrice, setMinPrice] = useState();
  const [maxPrice, setMaxPrice] = useState();
  const [sortCriteria, setSortCriteria] = useState();

  const [keyword, setKeyword] = useState();

  const [currentPage, setCurrentPage] = useState(1);

  const [minHeight, setMinHeight] = useState();
  const [maxHeight, setMaxHeight] = useState();
  const [minLength, setMinLength] = useState();
  const [maxLength, setMaxLength] = useState();
  const [minDepth, setMinDepth] = useState();
  const [maxDepth, setMaxDepth] = useState();
  const [designer, setDesigner] = useState();
  const [product_features, setProductFeature] = useState();

  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);

    const page = parseInt(newParams?.get("page"), 10) || 1;
    setCurrentPage(page);
  }, [searchParams, pathname]);

  const getListings = (newParams) => {
    let userToken = "";

    if (auth?.isAuthenticated) {
      userToken = auth?.user?._id;
    }

    LOAD_LISTINGS({
      page: currentPage || 1,
      locationSearch: newParams,
      userToken: userToken,
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const onPageChangeHandler = (page) => {
    let newParams = new URLSearchParams(window.location.search);

    // Update or delete the 'page' parameter based on the page number
    if (page === 1) {
      newParams.delete("page");
    } else {
      newParams.set("page", page);
    }

    // Push the new URL with the updated query parameters
    router.push(pathname + "?" + newParams.toString());

    setCurrentPage(page);
  };

  const deletePageHandler = () => {
    setCurrentPage(1);
    let newParams = new URLSearchParams(
      window.location.search.replace(/amp%3B/g, "&")
    );
    newParams.delete("page");
    router.push(pathname + "?" + newParams.toString());
  };

  useEffect(() => {
    let filterParams = {
      category: categoryParam || searchParams?.get("category"),
      option: optionParam || searchParams?.get("option"),
      subType: typeParam || searchParams?.get("subType"),
      brand: brandParam || searchParams?.get("brand"),
      location: locationParam || searchParams?.get("location"),
      style: styleParam ? styleParam : searchParams?.get("style"),
      room: roomParam ? roomParam : searchParams?.get("room"),
      material: searchParams?.get("material"),
      color: searchParams?.get("color"),
      condition: searchParams?.get("condition"),
      minPrice: searchParams?.get("minPrice"),
      maxPrice: searchParams?.get("maxPrice"),
      sort: searchParams?.get("sort"),
      keyword: searchParams?.get("keyword"),
      minHeight: searchParams?.get("minHeight"),
      maxHeight: searchParams?.get("maxHeight"),
      minLength: searchParams?.get("minLength"),
      maxLength: searchParams?.get("maxLength"),
      minDepth: searchParams?.get("minDepth"),
      maxDepth: searchParams?.get("maxDepth"),
      op: searchParams?.get("op"),
      tags: searchParams?.get("tags"),
      designer: searchParams?.get("designer"),
      discount: searchParams?.get("discount"),
      product_features: searchParams?.get("product_features"),
    };

    const setters = {
      category: setCategory,
      option: setOption,
      subType: setSubType,
      brand: setBrand,
      location: setLocationCity,
      style: setStyle,
      room: setRoom,
      color: setColor,
      material: setMaterial,
      condition: setCondition,
      minPrice: setMinPrice,
      maxPrice: setMaxPrice,
      sort: setSortCriteria,
      keyword: setKeyword,
      minHeight: setMinHeight,
      maxHeight: setMaxHeight,
      minLength: setMinLength,
      maxLength: setMaxLength,
      minDepth: setMinDepth,
      maxDepth: setMaxDepth,
      op: setOp,
      tags: setTag,
      designer: setDesigner,
      discount: setDiscount,
      product_features: setProductFeature,
    };

    let filters = [];

    Object.keys(filterParams).forEach((key) => {
      setters[key](filterParams[key]);
      if (filterParams[key]) {
        // Add an object for each filter parameter to the filters array
        filters.push({
          attribute: key,
          value: filterParams[key],
        });
      }
    });
    // if (
    // 	process.env.NEXT_PUBLIC_NODE_ENV === 'production' &&
    // 	filters.length > 0
    // ) {
    // 	filterListener(filters);
    // }

    filterParams = Object.fromEntries(
      Object.entries(filterParams).filter(
        ([key, value]) =>
          value !== null && value !== "" && value !== "undefined"
      )
    );

    const paramsString = new URLSearchParams(filterParams).toString();

    const isBot = /bot|crawl|slurp|spider|Prerender|facebookexternalhit/i.test(
      navigator?.userAgent
    );

    if (!isBot) getListings(paramsString);
  }, [
    categoryParam,
    optionParam,
    typeParam,
    brandParam,
    locationParam,
    styleParam,
    roomParam,
    currentPage,
    searchParams,
    pathname,
  ]);

  const locations2 = [
    {
      id: "LOC",
      area: "Buy Local",
      lat:
        isClient && localStorage.getItem("geoLocationLat")
          ? localStorage.getItem("geoLocationLat")
          : "",
      long:
        isClient && localStorage.getItem("geoLocationLong")
          ? localStorage.getItem("geoLocationLong")
          : "",
    },
    ...locations,
  ];

  const capitalizeEachWord = (text) => {
    // Direct replacement for special cases
    const specialCases = {
      "1980_s": "1980s",
      "1990_s": "1990s",
      "20_th_century": "20th Century",
    };

    if (specialCases[text]) {
      return specialCases[text];
    }

    // For words that aren't special cases
    return (
      text
        .replace(/_/g, " ")
        .replace(/-/g, " ")
        // .replace(/and/g, "&")
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    );
  };

  const clearFilterHandler = (e) => {
    // e.preventDefault();
    // setSortCriteria();
    // setKeyword();
    // setMaxPrice();
    // !categoryParam && setCategory();
    // !optionParam && setOption();
    // !typeParam && setSubType();
    // setTag();
    // setColor();
    // !locationParam && setLocationCity();
    // !roomParam && setRoom();
    // setMaxPrice();
    // setMinPrice();
    // setCondition();
    // !brandParam && setBrand();
    // setOp();
    // !styleParam && setStyle();
    // setDiscount();
    // setMaterial();
    // setMinHeight();
    // setMaxHeight();
    // setMinLength();
    // setMaxLength();
    // setMinDepth();
    // setMaxDepth();
    // setDesigner();
    // setProductFeature();
    setActive(false);
    setShow(false);
    router.replace(pathname, undefined, { shallow: true });
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // Clear!

  const clearHandler = (e, itemToSet, param) => {
    e.preventDefault();

    let newParams = new URLSearchParams(
      window.location.search.replace(/amp%3B/g, "&")
    );

    newParams?.delete("page");
    if (param === "category") {
      newParams?.delete("subType");
      newParams?.delete("option");
      newParams?.delete("category");
    } else if (param === "option") {
      newParams?.delete("subType");
      newParams?.delete("option");
    } else if (param === "subType") {
      newParams?.delete("subType");
    } else if (param === "height") {
      newParams?.delete("minHeight");
      newParams?.delete("maxHeight");
    } else if (param === "length") {
      newParams?.delete("minLength");
      newParams?.delete("maxLength");
    } else if (param === "depth") {
      newParams?.delete("minDepth");
      newParams?.delete("maxDepth");
    } else {
      newParams?.delete(param);
    }

    router.replace(pathname + "?" + newParams.toString());
  };

  // end

  useEffect(() => {
    const searchVariables = [
      keyword,
      maxPrice,
      minPrice,
      condition,
      !categoryParam && category,
      !optionParam && option,
      !typeParam && subType,
      !brandParam && brand,
      !roomParam && room,
      !styleParam && style,
      material,
      !locationParam && locationCity,
      sortCriteria,
      op,
      color,
      tag,
      discount,
      minHeight,
      maxHeight,
      minLength,
      maxLength,
      minDepth,
      maxDepth,
      designer,
      product_features,
      //   currentPage === 1 ? null : currentPage,
    ];

    if (searchVariables.some(Boolean)) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      setActive(true);
    } else {
      setActive(false);
    }
  }, [
    keyword,
    maxPrice,
    minPrice,
    condition,
    category,
    option,
    subType,
    locationCity,
    sortCriteria,
    brand,
    op,
    color,
    tag,
    style,
    material,
    discount,
    room,
    minHeight,
    maxHeight,
    minLength,
    maxLength,
    minDepth,
    maxDepth,
    designer,
    product_features,
    // currentPage,
  ]);

  const [locationDataClosest, setLocationDataClosest] = useState({
    loading: true,
    city: "",
    postalCode: "",
    region: "",
    longitude: "",
    latitude: "",
  });

  const [run, setRun] = useState(0);
  useEffect(() => {
    const isBot = /bot|crawl|slurp|spider|Prerender|facebookexternalhit/i.test(
      navigator?.userAgent
    );

    const getLoc = async () => {
      if (
        localStorage.getItem("lat") &&
        localStorage.getItem("long") &&
        localStorage.getItem("postalCode") &&
        localStorage.getItem("postalCode") !== "undefined"
      ) {
        setLocationDataClosest({
          loading: false,
          city: "",
          postalCode: localStorage.getItem("postalCode"),
          region: "",
          longitude: localStorage.getItem("long"),
          latitude: localStorage.getItem("lat"),
        });
      } else if (run < 4) {
        setRun(run + 1);
        await axios
          .get(
            `${process.env.NEXT_PUBLIC_SERVER_API}/api/other/location-ipInfo`
          )
          .then((response) => {
            const data = response?.data?.data;
            const [latitude, longitude] = data.loc?.split(",");

            setLocationDataClosest({
              loading: false,
              city: data.city || "",
              postalCode: data.postal || "",
              region: data.region || "",
              longitude: longitude || "",
              latitude: latitude || "",
            });
            localStorage.setItem("lat", latitude);
            localStorage.setItem("long", longitude);
            localStorage.setItem("postalCode", data.postal);
            let region = getLocation(latitude, longitude);
            if (region) {
              localStorage.setItem("region", region);

              const paramsString = new URLSearchParams().toString();
              getListings(paramsString);
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    };

    if (!isBot) {
      getLoc();
    }
  }, []);

  const urlHandler = (filter, param) => {
    let newParams = new URLSearchParams(window.location.search);
    newParams.delete(filter);
    newParams.append(filter, param);

    // Push the new URL with pathname and query object
    router.push(pathname + "?" + newParams.toString());
  };

  useEffect(() => {
    deletePageHandler();
  }, [
    category,
    option,
    subType,
    brand,
    locationCity,
    sortCriteria,
    op,
    discount,
    keyword,
    color,
    material,
    room,
    style,
    condition,
    minHeight,
    maxHeight,
    minLength,
    maxLength,
    minDepth,
    maxDepth,
    designer,
    product_features,
  ]);

  const formatSegmentText = (segment) => {
    // Check if segment is a location
    const matchingLocation = locations.find(
      (loc) => loc.id.toLowerCase() === segment.toLowerCase()
    );

    // If a matching location is found, return its area
    if (matchingLocation) {
      return matchingLocation.area;
    }
    return segment
      .replace(/-/g, " ")
      .replace(/_/g, " ")
      .replace(/\band\b/g, "&")
      .replace("_", " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const getUrlToSegment = (index) => {
    return "/" + segments.slice(0, index + 1).join("/");
  };

  return (
    <>
      <div className={classes.bg}>
        <section className={classes.mainNavigation}>
          {/* Always show a link to home/collections */}
          <Link href="/collections" className={classes.mainNavigationText}>
            Collections
          </Link>

          {segments?.map(
            (segment, index) =>
              segment !== "collections" && (
                <React.Fragment key={index}>
                  <ChevronRight className={classes.categorySeparator} />
                  <Link
                    href={getUrlToSegment(index)}
                    className={
                      index !== segments.length - 1
                        ? classes.mainNavigationText
                        : classes.mainNavigationTextActive
                    }
                  >
                    {formatSegmentText(segment)}
                  </Link>
                </React.Fragment>
              )
          )}
          {segments?.length <= 1 && (
            <>
              <ChevronRight className={classes.categorySeparator} />
              <span className={classes.mainNavigationTextActive}>All</span>
            </>
          )}
        </section>

        <CategoriesDiscover
          category={category}
          option={option}
          subType={subType}
          brand={brand}
          locationCity={locationCity}
          sortCriteria={sortCriteria}
          op={op}
          room={room}
          style={style}
          sale={discount}
          searchTerm={keyword}
          uniqueBrandList={uniqueBrands}
          handleShow={() => setShow(true)}
        />
        {active && (
          <button className={classes.mobileDelete} onClick={clearFilterHandler}>
            Clear Filters
          </button>
        )}

        <div className={classes.showPageContainer}>
          <div className={classes.filterBox}>
            <h6 className={classes.filterBy}>
              <div className={classes.gap05}>
                Filter by{" "}
                {active && (
                  <div
                    onClick={clearFilterHandler}
                    className={classes.delFilters}
                  >
                    Clear Filters
                  </div>
                )}
              </div>

              <div className={classes.listingLength}>
                {listings?.paginationData?.totalListings?.toLocaleString(
                  undefined,
                  { maximumFractionDigits: 2 }
                )}{" "}
                results
              </div>
            </h6>

            {active && (
              <section className={classes.filterSection}>
                {category && !categoryParam && (
                  <div
                    className={classes.badge}
                    onClick={(e) => clearHandler(e, setCategory, "category")}
                  >
                    <p className={`${classes.badgeText} `}>
                      {capitalizeEachWord(category)}
                      <XCircle className={classes.deleteIcon} />
                    </p>
                  </div>
                )}
                {op && (
                  <div
                    className={classes.badge}
                    onClick={(e) => clearHandler(e, setOp, "op")}
                  >
                    <p className={`${classes.badgeText} `}>
                      {capitalizeEachWord(op)}
                      <XCircle className={classes.deleteIcon} />
                    </p>
                  </div>
                )}

                {tag && (
                  <div
                    className={classes.badge}
                    onClick={(e) => clearHandler(e, setTag, "tags")}
                  >
                    <p className={`${classes.badgeText} `}>
                      {tag}
                      <XCircle className={classes.deleteIcon} />
                    </p>
                  </div>
                )}

                {color && (
                  <div
                    className={classes.badge}
                    onClick={(e) => clearHandler(e, setColor, "color")}
                  >
                    <div
                      className={classes.colorRound}
                      style={{
                        background: colorArray.find((col) => col.name === color)
                          ?.code,
                      }}
                    />
                    <p className={`${classes.badgeText} `}>
                      {color}
                      <XCircle className={classes.deleteIcon} />
                    </p>
                  </div>
                )}

                {locationCity && !locationParam && (
                  <div
                    className={classes.badge}
                    onClick={(e) =>
                      clearHandler(e, setLocationCity, "location")
                    }
                  >
                    <p className={`${classes.badgeText} `}>
                      {locationCity &&
                        locations2.map(
                          (loc) =>
                            loc.id.toLowerCase() === locationCity && loc.area
                        )}{" "}
                      {locationCity === "LOC" &&
                        locationDataClosest.postalCode && (
                          <>at {locationDataClosest.postalCode}</>
                        )}
                      <XCircle className={classes.deleteIcon} />
                    </p>
                  </div>
                )}
                {option && !optionParam && (
                  <div
                    className={classes.badge}
                    onClick={(e) => clearHandler(e, setOption, "option")}
                  >
                    <p className={`${classes.badgeText}`}>
                      {capitalizeEachWord(option)}{" "}
                      <XCircle className={classes.deleteIcon} />
                    </p>
                  </div>
                )}
                {subType && !typeParam && (
                  <div
                    className={classes.badge}
                    onClick={(e) => clearHandler(e, setSubType, "subType")}
                  >
                    <p className={`${classes.badgeText}`}>
                      {capitalizeEachWord(subType)}{" "}
                      <XCircle className={classes.deleteIcon} />
                    </p>
                  </div>
                )}
                {condition && (
                  <div
                    className={classes.badge}
                    onClick={(e) => clearHandler(e, setCondition, "condition")}
                  >
                    <p className={`${classes.badgeText}`}>
                      {condition} <XCircle className={classes.deleteIcon} />
                    </p>
                  </div>
                )}

                {style && !styleParam && (
                  <div
                    key={style}
                    className={classes.badge}
                    onClick={(e) => clearHandler(e, setStyle, "style")}
                  >
                    <p className={`${classes.badgeText} `}>
                      {capitalizeEachWord(style)}
                      <XCircle className={classes.deleteIcon} />
                    </p>
                  </div>
                )}

                {material && (
                  <div
                    className={classes.badge}
                    onClick={(e) => clearHandler(e, setMaterial, "material")}
                  >
                    <p className={`${classes.badgeText} `}>
                      {capitalizeEachWord(material)}
                      <XCircle className={classes.deleteIcon} />
                    </p>
                  </div>
                )}

                {room && !roomParam && (
                  <div
                    className={classes.badge}
                    onClick={(e) => clearHandler(e, setRoom, "room")}
                  >
                    <p className={`${classes.badgeText} `}>
                      {capitalizeEachWord(room)} Furniture
                      <XCircle className={classes.deleteIcon} />
                    </p>
                  </div>
                )}

                {sold && (
                  <div
                    className={classes.badge}
                    onClick={(e) => clearHandler(e, setSold, "sold")}
                  >
                    <p className={`${classes.badgeText} `}>
                      Sold Items
                      <XCircle className={classes.deleteIcon} />
                    </p>
                  </div>
                )}
                {minPrice && (
                  <div
                    className={classes.badge}
                    onClick={(e) => clearHandler(e, setMinPrice, "minPrice")}
                  >
                    <p className={`${classes.badgeText} `}>
                      min ${minPrice} <XCircle className={classes.deleteIcon} />
                    </p>
                  </div>
                )}
                {discount && (
                  <div
                    className={classes.badge}
                    onClick={(e) => clearHandler(e, setDiscount, "discount")}
                  >
                    <p className={`${classes.badgeText} `}>
                      {discount >= 40 ? "Our Super Sales" : "On Sale"}
                      <XCircle className={classes.deleteIcon} />
                    </p>
                  </div>
                )}
                {maxPrice && (
                  <div
                    className={classes.badge}
                    onClick={(e) => clearHandler(e, setMaxPrice, "maxPrice")}
                  >
                    <p className={`${classes.badgeText} `}>
                      max ${maxPrice} <XCircle className={classes.deleteIcon} />
                    </p>
                  </div>
                )}
                {minHeight || maxHeight ? (
                  <div
                    className={classes.badge}
                    onClick={(e) => clearHandler(e, "height", "height")}
                    // Adjust the clearHandler's signature if necessary to clear the dimension properly
                  >
                    <p className={`${classes.badgeText} `}>
                      {minHeight && `${minHeight}"H `}
                      {maxHeight &&
                        (minHeight ? `- ${maxHeight}"H` : `${maxHeight}"H`)}
                      <XCircle className={classes.deleteIcon} />
                    </p>
                  </div>
                ) : null}

                {minLength || maxLength ? (
                  <div
                    className={classes.badge}
                    onClick={(e) => clearHandler(e, "length", "length")}
                    // Adjust the clearHandler's signature if necessary to clear the dimension properly
                  >
                    <p className={`${classes.badgeText} `}>
                      {minLength && `${minLength}"L `}
                      {maxLength &&
                        (minLength ? `- ${maxLength}"L` : `${maxLength}"L`)}
                      <XCircle className={classes.deleteIcon} />
                    </p>
                  </div>
                ) : null}

                {minDepth || maxDepth ? (
                  <div
                    className={classes.badge}
                    onClick={(e) => clearHandler(e, "depth", "depth")}
                    // Adjust the clearHandler's signature if necessary to clear the dimension properly
                  >
                    <p className={`${classes.badgeText} `}>
                      {minDepth && `${minDepth}"D `}
                      {maxDepth &&
                        (minDepth ? `- ${maxDepth}"D` : `${maxDepth}"D`)}
                      <XCircle className={classes.deleteIcon} />
                    </p>
                  </div>
                ) : null}

                {sortCriteria && sortCriteria != "0" && (
                  <div
                    className={classes.badge}
                    onClick={(e) => clearHandler(e, setSortCriteria, "sort")}
                  >
                    <p className={`${classes.badgeText} `}>
                      {sortCriteria === "1" ? (
                        <>Price Low To High</>
                      ) : sortCriteria === "2" ? (
                        <>Price High To Low</>
                      ) : sortCriteria === "3" ? (
                        <>Newest</>
                      ) : sortCriteria === "4" ? (
                        <>Oldest</>
                      ) : sortCriteria === "5" ? (
                        <>Most Popular</>
                      ) : sortCriteria === "6" ? (
                        <>Best Deals</>
                      ) : sortCriteria === "7" ? (
                        <>Closest</>
                      ) : (
                        ""
                      )}
                      <XCircle className={classes.deleteIcon} />
                    </p>
                  </div>
                )}
                {keyword && (
                  <div
                    className={classes.badge}
                    onClick={(e) => clearHandler(e, setKeyword, "keyword")}
                  >
                    <p className={`${classes.badgeText} `}>
                      {capitalizeEachWord(keyword)}
                      <XCircle className={classes.deleteIcon} />
                    </p>
                  </div>
                )}
                {brand && !brandParam && (
                  <div
                    className={classes.badge}
                    onClick={(e) => clearHandler(e, setBrand, "brand")}
                  >
                    <p className={`${classes.badgeText} `}>
                      {" "}
                      {capitalizeEachWord(brand)}
                      <XCircle className={classes.deleteIcon} />
                    </p>
                  </div>
                )}
                {designer && (
                  <div
                    className={classes.badge}
                    onClick={(e) => clearHandler(e, setDesigner, "designer")}
                  >
                    <p className={`${classes.badgeText} `}>
                      {" "}
                      {capitalizeEachWord(designer)}
                      <XCircle className={classes.deleteIcon} />
                    </p>
                  </div>
                )}
                {product_features && (
                  <div
                    className={classes.badge}
                    onClick={(e) =>
                      clearHandler(e, setProductFeature, "product_features")
                    }
                  >
                    <p className={`${classes.badgeText} `}>
                      {" "}
                      {capitalizeEachWord(product_features)}
                      <XCircle className={classes.deleteIcon} />
                    </p>
                  </div>
                )}
              </section>
            )}
            {!keyword && (
              <NewCategoriesFilter
                theOption={option}
                theCategory={category}
                theSubType={subType}
                uniqueCategories={uniqueCategories}
                uniqueSubCategories={uniqueSubCategories}
                uniqueTypes={uniqueTypes}
                clearHandler={clearHandler}
              />
            )}
            <BrandFilter
              theBrand={brand}
              uniqueBrandList={uniqueBrands}
              clearHandler={clearHandler}
            />
            <LocationFilter
              theLocation={locationCity}
              uniqueLocations={listings.uniqueRegions}
              clearHandler={clearHandler}
            />
            <PriceFilter theMinPrice={minPrice} theMaxPrice={maxPrice} />
            <DimensionsFilter />

            <StyleFilter theStyle={style} uniqueStyle={uniqueStyles} />
            <MaterialFilter
              theMaterial={material}
              uniqueMaterials={uniqueMaterials}
            />

            <ColorFilter theColor={color} />
          </div>

          <div className={classes.rightSection}>
            <div className={classes.mainFilterContainer}>
              <div className={classes.badgeBox}>
                {locationDataClosest.postalCode &&
                  locationDataClosest.latitude &&
                  locationDataClosest.longitude && (
                    <div
                      onClick={
                        sortCriteria == 7
                          ? (e) =>
                              clearHandler(e, setSortCriteria, "sortCriteria")
                          : () => urlHandler("sort", 7)
                      }
                      className={
                        sortCriteria == 7
                          ? classes.badgeActionActive
                          : classes.badgeAction
                      }
                    >
                      <p className={classes.badgeText}>
                        Closest to {locationDataClosest.postalCode}
                      </p>
                    </div>
                  )}
                <div
                  className={
                    op === "branded"
                      ? classes.badgeActionActive
                      : classes.badgeAction
                  }
                  onClick={
                    op === "branded"
                      ? (e) => clearHandler(e, setOp, "op")
                      : () => urlHandler("op", "branded")
                  }
                >
                  <p className={classes.badgeText}>Branded</p>
                </div>
                <div
                  className={
                    op === "Free Shipping"
                      ? classes.badgeActionActive
                      : classes.badgeAction
                  }
                  onClick={
                    op === "Free Shipping"
                      ? (e) => clearHandler(e, setOp, "op")
                      : () => urlHandler("op", "Free Shipping")
                  }
                >
                  <p className={classes.badgeText}>Free shipping</p>
                </div>
                <div
                  className={
                    maxPrice === "500"
                      ? classes.badgeActionActive
                      : classes.badgeAction
                  }
                  onClick={
                    maxPrice === "500"
                      ? (e) => clearHandler(e, setMaxPrice, "maxPrice")
                      : () => urlHandler("maxPrice", 500)
                  }
                >
                  <p className={classes.badgeText}>Under $500</p>
                </div>
                <div
                  onClick={
                    discount === "20"
                      ? (e) => clearHandler(e, setDiscount, "discount")
                      : () => urlHandler("discount", 20)
                  }
                  className={
                    discount === "20"
                      ? classes.badgeActionActive
                      : classes.badgeAction
                  }
                >
                  <p className={classes.badgeText}>On Sale</p>
                </div>
              </div>
              <div className={classes.saveAndSort}>
                <SavedSearches
                  className={classes.saveSearchWrapper}
                  category={category}
                  option={option}
                  type={subType}
                  brand={brand}
                  minPrice={minPrice}
                  maxPrice={maxPrice}
                  condition={condition}
                  material={material}
                  style={style}
                  keyword={keyword}
                />
                <div className={classes.sortingWrapper}>
                  <Sorting theSortCriteria={sortCriteria} />
                </div>
              </div>
            </div>

            <div className={classes.alignCenter}>
              {listings.loading ? (
                <>
                  {Array.from({ length: 8 }, (i, idx) => (
                    <ul className={classes.observe} key={idx}>
                      <li className={classes.listing}>
                        <PlaceHolderListingNew />
                      </li>
                    </ul>
                  ))}
                </>
              ) : listings?.listings?.length > 0 ? (
                <>
                  {listings.listings.map((item, index) => (
                    <ul className={classes.observe} key={index} id={item._id}>
                      {listings.listings.length === index + 1 ? (
                        <>
                          {item.title && (
                            <li className={classes.listing} key={item._id}>
                              <Listing
                                listing={item}
                                idx={index + (currentPage - 1) * 52}
                              />
                            </li>
                          )}

                          {item.type === "message" && (
                            <li className={classes.listing} key={item.message}>
                              <MessageCard item={item} />
                            </li>
                          )}
                        </>
                      ) : (
                        <>
                          {item.title && (
                            <li className={classes.listing} key={item._id}>
                              <Listing
                                listing={item}
                                idx={index + (currentPage - 1) * 52}
                              />
                            </li>
                          )}

                          {item.type === "message" && (
                            <li className={classes.listing} key={item.message}>
                              <MessageCard item={item} />
                            </li>
                          )}
                        </>
                      )}
                    </ul>
                  ))}
                </>
              ) : (
                !listings.loading && (
                  <div className={classes.buySomeShit}>
                    <Image
                      width={120}
                      height={120}
                      src={
                        "https://ik.imagekit.io/kuj9zqlrx0or/Static/emptyBox.png"
                      }
                      className={classes.emptyBox}
                      alt="No consignment offers used furnitue Kashew"
                    />
                    <h3 className={classes.buyText}>
                      Ups, we dont have that (yet)!
                    </h3>
                    <div
                      className={classes.buyLink}
                      onClick={(e) => clearFilterHandler(e)}
                    >
                      Explore Kashew
                    </div>
                  </div>
                )
              )}
            </div>
            <BuyPaginationBar
              paginationData={listings.paginationData}
              onPageChange={onPageChangeHandler}
              loading={listings.loading}
            />

            <RecentlyViewed />
          </div>
        </div>
      </div>
      <Offcanvas
        show={show}
        placement="end"
        className={classes.Modal}
        scroll={false}
        onHide={() => setShow(false)}
      >
        <Offcanvas.Header className={classes.modalHeader}>
          <section className={classes.topHeaderModal}>
            <Offcanvas.Title onClick={() => setShow(false)}>
              <X className={classes.modalClose} />
            </Offcanvas.Title>
            <Offcanvas.Title className={classes.modalHeaderText}>
              Filter
            </Offcanvas.Title>
            <Offcanvas.Title
              onClick={clearFilterHandler}
              className={classes.modalCloseText}
            >
              Reset
            </Offcanvas.Title>
          </section>
          {active && (
            <section className={classes.filterBubbleSectionMobile}>
              {category && !categoryParam && (
                <div
                  className={classes.badge}
                  onClick={(e) => clearHandler(e, setCategory, "category")}
                >
                  <p className={`${classes.badgeText} `}>
                    {capitalizeEachWord(category)}
                    <XCircle className={classes.deleteIcon} />
                  </p>
                </div>
              )}
              {op && (
                <div
                  className={classes.badge}
                  onClick={(e) => clearHandler(e, setOp, "op")}
                >
                  <p className={`${classes.badgeText} `}>
                    {capitalizeEachWord(op)}
                    <XCircle className={classes.deleteIcon} />
                  </p>
                </div>
              )}

              {tag && (
                <div
                  className={classes.badge}
                  onClick={(e) => clearHandler(e, setTag, "tag")}
                >
                  <p className={`${classes.badgeText} `}>
                    {tag}
                    <XCircle className={classes.deleteIcon} />
                  </p>
                </div>
              )}

              {color && (
                <div
                  className={classes.badge}
                  onClick={(e) => clearHandler(e, setColor, "color")}
                >
                  <div
                    className={classes.colorRound}
                    style={{
                      background: colorArray.find((col) => col.name === color)
                        ?.code,
                    }}
                  />
                  <p className={`${classes.badgeText} `}>
                    {color}
                    <XCircle className={classes.deleteIcon} />
                  </p>
                </div>
              )}

              {locationCity && !locationParam && (
                <div
                  className={classes.badge}
                  onClick={(e) => clearHandler(e, setLocationCity, "location")}
                >
                  <p className={`${classes.badgeText} `}>
                    {locationCity &&
                      locations2.map(
                        (loc) =>
                          loc.id.toLowerCase() === locationCity && loc.area
                      )}{" "}
                    {/* {locationCity === "LOC" && postCode && <>at {postCode}</>} */}
                    <XCircle className={classes.deleteIcon} />
                  </p>
                </div>
              )}
              {option && !optionParam && (
                <div
                  className={classes.badge}
                  onClick={(e) => clearHandler(e, setOption, "option")}
                >
                  <p className={`${classes.badgeText} `}>
                    {capitalizeEachWord(option)}{" "}
                    <XCircle className={classes.deleteIcon} />
                  </p>
                </div>
              )}
              {subType && !typeParam && (
                <div
                  className={classes.badge}
                  onClick={(e) => clearHandler(e, setSubType, "subType")}
                >
                  <p className={`${classes.badgeText} `}>
                    {capitalizeEachWord(subType)}{" "}
                    <XCircle className={classes.deleteIcon} />
                  </p>
                </div>
              )}
              {condition && (
                <div
                  className={classes.badge}
                  onClick={(e) => clearHandler(e, setCondition, "condition")}
                >
                  <p className={`${classes.badgeText} `}>
                    {condition} <XCircle className={classes.deleteIcon} />
                  </p>
                </div>
              )}

              {style && !styleParam && (
                <div
                  key={style}
                  className={classes.badge}
                  onClick={(e) => clearHandler(e, setStyle, "style")}
                >
                  <p className={`${classes.badgeText} `}>
                    {capitalizeEachWord(style)}
                    <XCircle className={classes.deleteIcon} />
                  </p>
                </div>
              )}

              {material && (
                <div
                  className={classes.badge}
                  onClick={(e) => clearHandler(e, setMaterial, "material")}
                >
                  <p className={`${classes.badgeText} `}>
                    {capitalizeEachWord(material)}
                    <XCircle className={classes.deleteIcon} />
                  </p>
                </div>
              )}

              {room && !roomParam && (
                <div
                  key={room}
                  className={classes.badge}
                  onClick={(e) => clearHandler(e, setRoom, "room")}
                >
                  <p className={`${classes.badgeText} `}>
                    {capitalizeEachWord(room)} Furniture
                    <XCircle className={classes.deleteIcon} />
                  </p>
                </div>
              )}

              {minPrice && (
                <div
                  className={classes.badge}
                  onClick={(e) => clearHandler(e, setMinPrice, "minPrice")}
                >
                  <p className={`${classes.badgeText} `}>
                    min ${minPrice} <XCircle className={classes.deleteIcon} />
                  </p>
                </div>
              )}
              {discount && (
                <div
                  className={classes.badge}
                  onClick={(e) => clearHandler(e, setDiscount, "discount")}
                >
                  <p className={`${classes.badgeText} `}>
                    {discount >= 40 ? "Our Super Sales" : "On Sale"}
                    <XCircle className={classes.deleteIcon} />
                  </p>
                </div>
              )}
              {maxPrice && (
                <div
                  className={classes.badge}
                  onClick={(e) => clearHandler(e, setMaxPrice, "maxPrice")}
                >
                  <p className={`${classes.badgeText} `}>
                    max ${maxPrice} <XCircle className={classes.deleteIcon} />
                  </p>
                </div>
              )}
              {minHeight || maxHeight ? (
                <div
                  className={classes.badge}
                  onClick={(e) => clearHandler(e, "height", "height")}
                  // Adjust the clearHandler's signature if necessary to clear the dimension properly
                >
                  <p className={`${classes.badgeText} `}>
                    {minHeight && `${minHeight}"H `}
                    {maxHeight &&
                      (minHeight ? `- ${maxHeight}"H` : `${maxHeight}"H`)}
                    <XCircle className={classes.deleteIcon} />
                  </p>
                </div>
              ) : null}

              {minLength || maxLength ? (
                <div
                  className={classes.badge}
                  onClick={(e) => clearHandler(e, "length", "length")}
                  // Adjust the clearHandler's signature if necessary to clear the dimension properly
                >
                  <p className={`${classes.badgeText} `}>
                    {minLength && `${minLength}"L `}
                    {maxLength &&
                      (minLength ? `- ${maxLength}"L` : `${maxLength}"L`)}
                    <XCircle className={classes.deleteIcon} />
                  </p>
                </div>
              ) : null}

              {minDepth || maxDepth ? (
                <div
                  className={classes.badge}
                  onClick={(e) => clearHandler(e, "depth", "depth")}
                  // Adjust the clearHandler's signature if necessary to clear the dimension properly
                >
                  <p className={`${classes.badgeText} `}>
                    {minDepth && `${minDepth}"D `}
                    {maxDepth &&
                      (minDepth ? `- ${maxDepth}"D` : `${maxDepth}"D`)}
                    <XCircle className={classes.deleteIcon} />
                  </p>
                </div>
              ) : null}

              {sortCriteria && (
                <div
                  className={classes.badge}
                  onClick={(e) => clearHandler(e, setSortCriteria, "sort")}
                >
                  <p className={`${classes.badgeText} `}>
                    {sortCriteria === "1" ? (
                      <>Price Low To High</>
                    ) : sortCriteria === "2" ? (
                      <>Price High To Low</>
                    ) : sortCriteria === "3" ? (
                      <>Newest</>
                    ) : sortCriteria === "4" ? (
                      <>Oldest</>
                    ) : sortCriteria === "5" ? (
                      <>Most Popular</>
                    ) : sortCriteria === "6" ? (
                      <>Best Deals</>
                    ) : sortCriteria === "7" ? (
                      <>Most Relevant</>
                    ) : (
                      ""
                    )}
                    <XCircle className={classes.deleteIcon} />
                  </p>
                </div>
              )}
              {keyword && (
                <div
                  className={classes.badge}
                  onClick={(e) => clearHandler(e, setKeyword, "keyword")}
                >
                  <p className={`${classes.badgeText} `}>
                    {capitalizeEachWord(keyword)}
                    <XCircle className={classes.deleteIcon} />
                  </p>
                </div>
              )}
              {brand && brandParam && (
                <div
                  className={classes.badge}
                  onClick={(e) => clearHandler(e, setBrand, "brand")}
                >
                  <p className={`${classes.badgeText} `}>
                    {" "}
                    {capitalizeEachWord(brand)}
                    <XCircle className={classes.deleteIcon} />
                  </p>
                </div>
              )}
              {designer && (
                <div
                  className={classes.badge}
                  onClick={(e) => clearHandler(e, setDesigner, "designer")}
                >
                  <p className={`${classes.badgeText} `}>
                    {" "}
                    {capitalizeEachWord(designer)}
                    <XCircle className={classes.deleteIcon} />
                  </p>
                </div>
              )}
              {product_features && (
                <div
                  className={classes.badge}
                  onClick={(e) =>
                    clearHandler(e, setProductFeature, "product_features")
                  }
                >
                  <p className={`${classes.badgeText} `}>
                    {" "}
                    {capitalizeEachWord(product_features)}
                    <XCircle className={classes.deleteIcon} />
                  </p>
                </div>
              )}
            </section>
          )}
        </Offcanvas.Header>

        <div className={classes.modalBody}>
          <Sorting theSortCriteria={sortCriteria} />
          {!keyword && (
            <NewCategoriesFilter
              theOption={option}
              theCategory={category}
              theSubType={subType}
              uniqueCategories={uniqueCategories}
              uniqueSubCategories={uniqueSubCategories}
              uniqueTypes={uniqueTypes}
              clearHandler={clearHandler}
            />
          )}
          <BrandFilter theBrand={brand} uniqueBrandList={uniqueBrands} />{" "}
          <LocationFilter
            theLocation={locationCity}
            uniqueLocations={listings.uniqueRegions}
            clearHandler={clearHandler}
          />
          <PriceFilter theMinPrice={minPrice} theMaxPrice={maxPrice} />
          <DimensionsFilter />
          <StyleFilter theStyle={style} uniqueStyle={uniqueStyles} />
          <MaterialFilter
            theMaterial={material}
            uniqueMaterials={uniqueMaterials}
          />
          <ColorFilter theColor={color} />
        </div>
        <div className={classes.applyButton}>
          <button
            onClick={() => setShow(false)}
            className={classes.applyFilter}
          >
            Apply Filters
          </button>
        </div>
      </Offcanvas>
    </>
  );
};

export default ShowListings;
